import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import './AdminStyles.css';

function UserRole() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    fullName: '',
    role: 'manager',
    isActive: true,
    pagePermissions: []
  });

  // All available pages
  const availablePages = [
    { id: 'dashboard', name: 'Dashboard' },
    { id: 'classes', name: 'Classes' },
    { id: 'chat', name: 'Chat' },
    { id: 'add-user', name: 'Add User' },
    { id: 'user-role', name: 'User Role' }
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_roles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      username: user.username || '',
      email: user.email || '',
      fullName: user.full_name || '',
      role: user.role || 'manager',
      isActive: user.is_active !== false,
      pagePermissions: user.page_permissions || []
    });
  };

  const togglePagePermission = (pageId) => {
    setEditForm(prev => ({
      ...prev,
      pagePermissions: prev.pagePermissions.includes(pageId)
        ? prev.pagePermissions.filter(id => id !== pageId)
        : [...prev.pagePermissions, pageId]
    }));
  };

  const handleUpdate = async () => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .update({
          username: editForm.username,
          email: editForm.email,
          full_name: editForm.fullName || null,
          role: editForm.role,
          page_permissions: editForm.pagePermissions,
          is_active: editForm.isActive,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingUser.id);

      if (error) throw error;

      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Failed to update user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('id', userId);

      if (error) throw error;

      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user');
    }
  };

  return (
    <div className="admin-layout">
      <div className="admin-main-content">
        <div className="main-header">
          <h1>User Role Management</h1>
        </div>
        <div className="main-content-body">
          {error && <div className="error-message">{error}</div>}

          {loading ? (
            <p>Loading users...</p>
          ) : (
            <div className="form-card">
              <h2>Users ({users.length})</h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f7fa', borderBottom: '2px solid #e9ecef' }}>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Username</th>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Email</th>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Full Name</th>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Role</th>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Pages</th>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                      <td style={{ padding: '12px' }}>{user.username}</td>
                      <td style={{ padding: '12px' }}>{user.email}</td>
                      <td style={{ padding: '12px' }}>{user.full_name || 'N/A'}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: user.role === 'head_teacher' ? '#8B5CF6' : '#10B981',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {user.role === 'head_teacher' ? 'Head Teacher' : 'Manager'}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {(user.page_permissions || []).map(pageId => {
                            const page = availablePages.find(p => p.id === pageId);
                            return page ? (
                              <span key={pageId} style={{
                                padding: '2px 6px',
                                backgroundColor: '#E3F2FD',
                                borderRadius: '3px',
                                fontSize: '11px'
                              }}>
                                {page.name}
                              </span>
                            ) : null;
                          })}
                          {(!user.page_permissions || user.page_permissions.length === 0) && (
                            <span style={{ color: '#999', fontSize: '12px' }}>No permissions</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: user.is_active ? '#4caf50' : '#999',
                          color: 'white',
                          fontSize: '12px'
                        }}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <button
                          onClick={() => handleEdit(user)}
                          style={{
                            padding: '6px 12px',
                            marginRight: '8px',
                            backgroundColor: '#2196f3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Edit Modal */}
          {editingUser && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '8px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}>
                <h2>Edit User</h2>
                <div className="form-group">
                  <label>Username *</label>
                  <input
                    type="text"
                    value={editForm.username}
                    onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={editForm.fullName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, fullName: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value }))}
                  >
                    <option value="head_teacher">Head Teacher</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={editForm.isActive}
                      onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                    />
                    Active
                  </label>
                </div>
                <div className="form-group">
                  <label>Page Permissions *</label>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
                    gap: '10px',
                    padding: '15px',
                    border: '1px solid #e9ecef',
                    borderRadius: '4px',
                    backgroundColor: '#f8f9fa'
                  }}>
                    {availablePages.map(page => (
                      <label 
                        key={page.id}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          cursor: 'pointer'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={editForm.pagePermissions.includes(page.id)}
                          onChange={() => togglePagePermission(page.id)}
                        />
                        <span>{page.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button type="button" onClick={handleUpdate}>
                    Update User
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setEditingUser(null)}
                    style={{ backgroundColor: '#999' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserRole;
